import React from 'react';
import {
    Card,
    CardContent,
    Typography,
    Grid,
    Box,
    Button,
    CircularProgress
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    DirectionsRun,
    Favorite,
    FavoriteBorder,
    Timer,
    TrendingDown
} from '@mui/icons-material';
import { t } from 'i18next';

import { useStressTests } from '../../../../hooks/useStressTest';
import StressTestCard from './StressTestCard';
import StatCard from './StatCard';
import OtherMedicalTestCard from './OtherMedicalTestCard';
import ECGTestCard from './ECGTestCard';

const normalizeStressTest = (test) => ({
    uuid: test.uuid,
    type: test.type,
    date: test.ts,
    data: test.data,
    duration: test.data?.results?.Tempo,
    stats: {
        fc_rest: test.data?.results?.['Repouso'] || test.data?.results?.['FC repouso'],
        fc_peak: test.data?.results?.['Pico FC'] || '—',
        fc_recovery_1min: test.data?.results?.['1º min Recup'] || '—',
        mets: test.data?.results?.METS || '—'
    }
});

const prepareData = (tests, key, isStat = false) =>
    tests
        .sort((a, b) => new Date(a.date) - new Date(b.date))
        .map((t) => {
            const rawValue = isStat ? t.stats?.[key] : t[key];
            const value = rawValue != null && !isNaN(rawValue) ? Number(rawValue) : null;
            return {
                ts: t.date,
                value
            };
        });

const useMultipleStressTests = (patient, types, noPagination = true) => {
    const queries = types.map((type) =>
        useStressTests(patient, null, type, noPagination)
    );

    const isLoading = queries.some((q) => q.isLoading);
    const data = queries.flatMap((q) => q.data || []);

    return { data, isLoading };
};

const StressTestsSection = ({ user, seeUserState }) => {
    const navigate = useNavigate();
    const location = useLocation();

    const { data: stressTestsRaw, isLoading: isLoadingStressTests } =
        useMultipleStressTests(user?.role_uuid, ['STRESS_TEST', 'STRESS_TEST_CP']);

    const stressTests = React.useMemo(
        () => stressTestsRaw.map(normalizeStressTest),
        [stressTestsRaw]
    );

    const { data: ecgTestsRaw, isLoading: isLoadingECGs } = useMultipleStressTests(
        user?.role_uuid,
        ['ECG_1L', 'ECG_12L', 'ECG_HOLTER']
    );

    const ecgTests = React.useMemo(
        () => ecgTestsRaw.map(normalizeStressTest),
        [ecgTestsRaw]
    );

    const { data: otherTestsRaw, isLoading: isLoadingOtherTests } =
        useMultipleStressTests(user?.role_uuid, [
            'ECO_CG',
            'ECO_CG_DOPPLER',
            'ABPM',
            'PSG',
            'BIOCHEMISTRY',
            'OTHER'
        ]);

    const otherTests = React.useMemo(
        () => otherTestsRaw.map(normalizeStressTest),
        [otherTestsRaw]
    );

    const [selectedTests, setSelectedTests] = React.useState([]);

    const [compareMode, setCompareMode] = React.useState(false);
    const activeTests = React.useMemo(() => {
        if (!compareMode) return stressTests;
        return stressTests.filter((t) => selectedTests.includes(t.uuid));
    }, [compareMode, selectedTests, stressTests]);

    const safe = (test, path, fallback = '—') => {
        try {
            return path.split('.').reduce((obj, key) => obj?.[key], test) ?? fallback;
        } catch {
            return fallback;
        }
    };

    const stats = [
        {
            key: 'duration',
            label: t('stress.testDuration'),
            unit: 'min',
            icon: <Timer sx={{ fontSize: 40, color: '#1976d2' }} />,
            current: safe(activeTests[0], 'duration'),
            previous: safe(activeTests[1], 'duration'),
            chart: prepareData(activeTests, 'duration')
        },
        {
            key: 'fc_rest',
            label: t('stress.resting'),
            unit: 'bpm',
            icon: <FavoriteBorder sx={{ fontSize: 40, color: '#1976d2' }} />,
            current: safe(activeTests[0], 'stats.fc_rest'),
            previous: safe(activeTests[1], 'stats.fc_rest'),
            chart: prepareData(activeTests, 'fc_rest', true)
        },
        {
            key: 'fc_peak',
            label: t('stress.maxHR'),
            unit: 'bpm',
            icon: <Favorite sx={{ fontSize: 40, color: '#1976d2' }} />,
            current: safe(activeTests[0], 'stats.fc_peak'),
            previous: safe(activeTests[1], 'stats.fc_peak'),
            chart: prepareData(activeTests, 'fc_peak', true)
        },
        {
            key: 'fc_recovery_1min',
            label: t('stress.recovery'),
            unit: 'bpm',
            icon: <TrendingDown sx={{ fontSize: 40, color: '#1976d2' }} />,
            current: safe(activeTests[0], 'stats.fc_recovery_1min'),
            previous: safe(activeTests[1], 'stats.fc_recovery_1min'),
            chart: prepareData(activeTests, 'fc_recovery_1min', true)
        },
        {
            key: 'mets',
            label: 'METS',
            unit: '',
            icon: <DirectionsRun sx={{ fontSize: 40, color: '#1976d2' }} />,
            current: safe(activeTests[0], 'stats.mets'),
            previous: safe(activeTests[1], 'stats.mets'),
            chart: prepareData(activeTests, 'mets', true)
        }
    ];

    const latestTestUuid = React.useMemo(() => {
        if (!stressTests.length) return null;
        return [...stressTests].sort((a, b) => new Date(b.date) - new Date(a.date))[0]
            .uuid;
    }, [stressTests]);

    const isLoading = isLoadingStressTests || isLoadingECGs || isLoadingOtherTests;

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" mt={4}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ mt: 2 }}>
            <Card sx={{ mb: 3 }}>
                <CardContent sx={{ backgroundColor: '#f9f9f9' }}>
                    <Box display="flex" justifyContent="space-between" mb={2}>
                        <Typography variant="h5" fontWeight={700}>
                            {t('stress.title4')}
                        </Typography>

                        <Box display="flex" gap={2}>
                            <Button
                                variant={compareMode ? 'contained' : 'outlined'}
                                onClick={() => {
                                    setCompareMode(!compareMode);
                                    setSelectedTests([]);
                                }}
                            >
                                {compareMode
                                    ? t('stress.cancelCompare')
                                    : t('stress.compare')}
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() =>
                                    navigate('/users/user/stress-test/create', {
                                        state: {
                                            patient: user,
                                            from: location,
                                            seeUserState
                                        }
                                    })
                                }
                            >
                                {t('userPage.addStressTest')}
                            </Button>
                        </Box>
                    </Box>

                    {stressTests.length === 0 && (
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            mb={3}
                        >
                            <Typography variant="body2" color="text.secondary">
                                {t('userPage.noStressTest')}
                            </Typography>
                        </Box>
                    )}

                    {stressTests.length > 0 && (
                        <Grid container spacing={2} mb={3}>
                            {stats.map((stat) => (
                                <Grid item xs={12} sm={6} md={4} lg={2.4} key={stat.key}>
                                    <StatCard {...stat} chartType="line" />
                                </Grid>
                            ))}
                        </Grid>
                    )}

                    <Grid container spacing={2}>
                        {[...stressTests].reverse().map((test) => (
                            <Grid item xs={12} md={6} lg={4} key={test.uuid}>
                                <StressTestCard
                                    type={test.type}
                                    date={test.date}
                                    data={test.data}
                                    duration={test.duration}
                                    selected={selectedTests.includes(test.uuid)}
                                    compareMode={compareMode}
                                    isLatest={test.uuid === latestTestUuid}
                                    onClick={() => {
                                        if (!compareMode) {
                                            navigate(
                                                `/users/user/stress-test/${test.uuid}`,
                                                {
                                                    state: {
                                                        from: location,
                                                        seeUserState
                                                    }
                                                }
                                            );
                                        } else {
                                            setSelectedTests((prev) =>
                                                prev.includes(test.uuid)
                                                    ? prev.filter(
                                                          (id) => id !== test.uuid
                                                      )
                                                    : [...prev, test.uuid]
                                            );
                                        }
                                    }}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </CardContent>
            </Card>

            {/* ECGs + Outros Exames */}
            <Grid container spacing={3}>
                {/* ECGs */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent sx={{ backgroundColor: '#f9f9f9', height: '100%' }}>
                            <Box display="flex" justifyContent="space-between" mb={2}>
                                <Typography variant="h5" fontWeight={700}>
                                    ECGs
                                </Typography>

                                <Button
                                    variant="outlined"
                                    onClick={() =>
                                        navigate('/users/user/medical-test/create', {
                                            state: {
                                                patient: user,
                                                category: 'ECG',
                                                from: location,
                                                seeUserState
                                            }
                                        })
                                    }
                                >
                                    {t('exams.addECG')}
                                </Button>
                            </Box>

                            {ecgTests.length === 0 && (
                                <Box
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                >
                                    <Typography variant="body2" color="text.secondary">
                                        {t('userPage.noECGTest')}
                                    </Typography>
                                </Box>
                            )}

                            <Box
                                sx={{
                                    maxHeight: 450,
                                    overflowY: 'auto',
                                    p: 2,

                                    scrollbarWidth: 'thin',
                                    scrollbarColor: '#0A579B transparent',

                                    '&::-webkit-scrollbar': {
                                        width: '8px'
                                    },

                                    '&::-webkit-scrollbar-track': {
                                        background: 'transparent'
                                    },

                                    '&::-webkit-scrollbar-thumb': {
                                        backgroundColor: '#0A579B',
                                        borderRadius: '4px'
                                    },

                                    '&::-webkit-scrollbar-thumb:hover': {
                                        backgroundColor: '#1976d2'
                                    }
                                }}
                            >
                                <Grid container spacing={2} direction="column">
                                    {ecgTests.map((test) => (
                                        <Grid item key={test.uuid}>
                                            <ECGTestCard
                                                type={test.type}
                                                date={test.date}
                                                data={test.data}
                                                onClick={() =>
                                                    navigate(
                                                        `/users/user/medical-test/${test.uuid}`
                                                    )
                                                }
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Outros Exames */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent sx={{ backgroundColor: '#f9f9f9', height: '100%' }}>
                            <Box display="flex" justifyContent="space-between" mb={2}>
                                <Typography variant="h5" fontWeight={700}>
                                    {t('stress.otherExams')}
                                </Typography>

                                <Button
                                    variant="outlined"
                                    onClick={() =>
                                        navigate('/users/user/medical-test/create', {
                                            state: {
                                                patient: user,
                                                category: 'OTHER'
                                            }
                                        })
                                    }
                                >
                                    {t('exams.addExam')}
                                </Button>
                            </Box>

                            {ecgTests.length === 0 && (
                                <Box
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                >
                                    <Typography variant="body2" color="text.secondary">
                                        {t('userPage.noTest')}
                                    </Typography>
                                </Box>
                            )}

                            <Box
                                sx={{
                                    maxHeight: 450,
                                    overflowY: 'auto',
                                    p: 2,

                                    scrollbarWidth: 'thin',
                                    scrollbarColor: '#0A579B transparent',

                                    '&::-webkit-scrollbar': {
                                        width: '8px'
                                    },

                                    '&::-webkit-scrollbar-track': {
                                        background: 'transparent'
                                    },

                                    '&::-webkit-scrollbar-thumb': {
                                        backgroundColor: '#0A579B',
                                        borderRadius: '4px'
                                    },

                                    '&::-webkit-scrollbar-thumb:hover': {
                                        backgroundColor: '#1976d2'
                                    },

                                    '&::-webkit-scrollbar-button:single-button': {
                                        display: 'none',
                                        width: 0,
                                        height: 0
                                    }
                                }}
                            >
                                <Grid container spacing={2} direction="column">
                                    {otherTests.map((test) => (
                                        <Grid item key={test.uuid}>
                                            <OtherMedicalTestCard
                                                type={test.type}
                                                date={test.date}
                                                data={test.data}
                                                onClick={() =>
                                                    navigate(
                                                        `/users/user/medical-test/${test.uuid}`
                                                    )
                                                }
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default StressTestsSection;
